import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Mail,
  MailOpen,
  MousePointerClick,
  BellOff,
  AlertTriangle,
  BarChart3,
  Send,
  Eye,
  ShieldCheck,
  Bot,
  Loader2,
  Video,
} from "lucide-react";
import {
  getSubscriberStats,
  getSubscriberEvents,
  SubscriberEvent,
  SubscriberStatsResponse,
} from "../services/subscriberEventsService";
import { ReactComponent as InfoIcon } from "../assets/icons/Info.svg";
import SubscriberCustomFields from "./SubscriberCustomFields";
import SubscriberSegments from "./SubscriberSegments";

const statusBadgeColors: Record<string, { bg: string; color: string }> = {
  Active: { bg: "#d1fae5", color: "#065f46" },
  Bounced: { bg: "#fee2e2", color: "#dc2626" },
  Unsubscribed: { bg: "#e5e7eb", color: "#374151" },
};

const EVENTS_PER_PAGE = 10;

const pulseKeyframes = `
@keyframes skeleton-pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}
`;

const SkeletonBlock: React.FC<{ width?: string; height?: string; style?: React.CSSProperties }> = ({
  width = "100%",
  height = "14px",
  style,
}) => (
  <div
    style={{
      width,
      height,
      backgroundColor: "#e2e8f0",
      borderRadius: "4px",
      animation: "skeleton-pulse 1.5s ease-in-out infinite",
      ...style,
    }}
  />
);

const SkeletonCard: React.FC = () => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: "8px",
      padding: "20px",
    }}
  >
    <SkeletonBlock width="60%" height="14px" style={{ marginBottom: "12px" }} />
    <SkeletonBlock width="40%" height="28px" />
  </div>
);

const SkeletonTimelineEvent: React.FC<{ isLast?: boolean }> = ({ isLast }) => (
  <div style={{ display: "flex", gap: "16px" }}>
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "24px",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: "32px",
          height: "32px",
          borderRadius: "50%",
          backgroundColor: "#e2e8f0",
          animation: "skeleton-pulse 1.5s ease-in-out infinite",
          flexShrink: 0,
        }}
      />
      {!isLast && (
        <div style={{ width: "2px", flexGrow: 1, background: "#e2e8f0", minHeight: "24px" }} />
      )}
    </div>
    <div style={{ flex: 1, paddingBottom: isLast ? "8px" : "24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", paddingTop: "5px" }}>
        <SkeletonBlock width="50%" height="14px" />
        <SkeletonBlock width="120px" height="13px" />
      </div>
    </div>
  </div>
);

const SubscriberDetail: React.FC = () => {
  const { subscriberId } = useParams<{ subscriberId: string }>();
  const [statsData, setStatsData] = useState<SubscriberStatsResponse | null>(null);
  const [events, setEvents] = useState<SubscriberEvent[]>([]);
  const [statsLoading, setStatsLoading] = useState<boolean>(true);
  const [eventsLoading, setEventsLoading] = useState<boolean>(true);
  const [eventsLoadingMore, setEventsLoadingMore] = useState<boolean>(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    if (!subscriberId) return;

    // Fetch stats and first page of events in parallel
    const fetchStats = async () => {
      try {
        setStatsLoading(true);
        const result = await getSubscriberStats(subscriberId);
        setStatsData(result);
        setStatsError(null);
      } catch (err: any) {
        console.error("Failed to fetch subscriber stats:", err);
        setStatsError(err.message || "Failed to load subscriber stats");
      } finally {
        setStatsLoading(false);
      }
    };

    const fetchEvents = async () => {
      try {
        setEventsLoading(true);
        const result = await getSubscriberEvents(subscriberId, 1, EVENTS_PER_PAGE);
        setEvents(result.events);
        setCurrentPage(1);
        setHasMore(result.has_more);
        setEventsError(null);
      } catch (err: any) {
        console.error("Failed to fetch subscriber events:", err);
        setEventsError(err.message || "Failed to load subscriber events");
      } finally {
        setEventsLoading(false);
      }
    };

    fetchStats();
    fetchEvents();
  }, [subscriberId]);

  const loadMoreEvents = useCallback(async () => {
    if (!subscriberId || eventsLoadingMore) return;
    const nextPage = currentPage + 1;
    try {
      setEventsLoadingMore(true);
      const result = await getSubscriberEvents(subscriberId, nextPage, EVENTS_PER_PAGE);
      setEvents((prev) => [...prev, ...result.events]);
      setCurrentPage(nextPage);
      setHasMore(result.has_more);
    } catch (err: any) {
      console.error("Failed to load more events:", err);
    } finally {
      setEventsLoadingMore(false);
    }
  }, [subscriberId, currentPage, eventsLoadingMore]);

  const getEventIconColor = (eventType: string) => {
    const type = eventType.toLowerCase();
    switch (type) {
      case "delivered":
        return "#065f46";
      case "opened":
        return "#1d4ed8";
      case "clicked":
        return "#7c3aed";
      case "unsubscribed":
        return "#374151";
      case "bounced":
        return "#dc2626";
      default:
        return "#374151";
    }
  };

  const getEventIcon = (eventType: string) => {
    const type = eventType.toLowerCase();
    switch (type) {
      case "delivered":
        return <Send size={16} />;
      case "opened":
        return <MailOpen size={16} />;
      case "clicked":
        return <MousePointerClick size={16} />;
      case "unsubscribed":
        return <BellOff size={16} />;
      case "bounced":
        return <AlertTriangle size={16} />;
      default:
        return <Mail size={16} />;
    }
  };

  const getEventLabel = (eventType: string) => {
    const type = eventType.toLowerCase();
    switch (type) {
      case "delivered":
        return "Delivered";
      case "opened":
        return "Opened";
      case "clicked":
        return "Clicked";
      case "unsubscribed":
        return "Unsubscribed";
      case "bounced":
        return "Bounced";
      default:
        return eventType;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatPercent = (rate: number) => {
    return `${(rate * 100).toFixed(1)}%`;
  };

  // Full error state (only if stats failed — events can fail independently)
  if (statsError && !statsData) {
    return (
      <div style={{ textAlign: "center", padding: "2rem", color: "#E53E3E" }}>
        {statsError || "Failed to load subscriber data"}
      </div>
    );
  }

  const badgeColors = statsData
    ? statusBadgeColors[statsData.status] || statusBadgeColors.Unsubscribed
    : statusBadgeColors.Active;

  return (
    <div>
      <style>{pulseKeyframes}</style>

      {/* Header */}
      <div style={{ marginBottom: "24px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "8px",
          }}
        >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          {statsLoading ? (
            <>
              <SkeletonBlock width="300px" height="32px" />
              <SkeletonBlock width="80px" height="24px" style={{ borderRadius: "9999px" }} />
            </>
          ) : statsData ? (
            <>
              <h1 style={{ margin: 0 }}>{statsData.email}</h1>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "4px 12px",
                  borderRadius: "9999px",
                  fontSize: "13px",
                  fontWeight: 600,
                  backgroundColor: badgeColors.bg,
                  color: badgeColors.color,
                }}
              >
                {statsData.status}
              </span>
            </>
          ) : null}
        </div>
        <div className="tooltip-container" style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: "6px", padding: "6px 12px", backgroundColor: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "6px", color: "#1e40af", fontSize: "13px", fontWeight: 500, cursor: "help", whiteSpace: "nowrap" }}>
          <InfoIcon style={{ width: "14px", height: "14px" }} />
          Struggling to understand email metrics?
          <div className="tooltip" style={{ width: "340px", whiteSpace: "normal", textAlign: "left", fontWeight: 400, right: 0, left: "auto", transform: "none" }}>
            Email open and click metrics can be misleading due to bot scanning and tracking pixel blocking. Reach out to <a href="mailto:team@withpotions.com" style={{ color: "#1e40af", fontWeight: 600 }}>team@withpotions.com</a> and we'll help you make sense of your data.
          </div>
        </div>
        </div>
        {statsLoading ? (
          <SkeletonBlock width="200px" height="14px" />
        ) : statsData ? (
          <p style={{ margin: 0, color: "#718096", fontSize: "14px" }}>
            Subscribed on {formatDate(statsData.subscribed_at)}
          </p>
        ) : null}
      </div>

      {/* Known issue banner */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "10px 14px",
          backgroundColor: "#fef3c7",
          border: "1px solid #fcd34d",
          borderRadius: "8px",
          marginBottom: "16px",
          fontSize: "13px",
          color: "#92400e",
        }}
      >
        <InfoIcon style={{ width: "14px", height: "14px", flexShrink: 0 }} />
        Verified clicks, unique opens, and unique clicks on this page may be inaccurate due to a known sync issue. Individual post analytics and segment data are not affected.
      </div>

      {/* Stat cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: "16px",
          marginBottom: "32px",
        }}
      >
        {statsLoading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : statsData ? (
          <>
            <div
              style={{
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  color: "#718096",
                  fontSize: "13px",
                  fontWeight: 500,
                  marginBottom: "8px",
                }}
              >
                <Send size={16} />
                Emails Delivered
              </div>
              <div style={{ fontSize: "28px", fontWeight: 700, color: "#1a202c" }}>
                {statsData.stats.total_delivered}
              </div>
            </div>

            <div
              style={{
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  color: "#718096",
                  fontSize: "13px",
                  fontWeight: 500,
                  marginBottom: "8px",
                }}
              >
                <Eye size={16} />
                Unique Opens
                <div className="tooltip-container" style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
                  <InfoIcon style={{ width: "14px", height: "14px", opacity: 0.4, cursor: "help" }} />
                  <div className="tooltip">
                    {statsData.stats.unique_opens === 0 && statsData.stats.total_clicks > 0
                      ? "This subscriber may block email open tracking. It's common to see clicks without opens when tracking pixels are blocked."
                      : "Distinct editions opened by this subscriber. Each edition counts at most once."}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: "28px", fontWeight: 700, color: "#1a202c" }}>
                {statsData.stats.unique_opens}
              </div>
              <div style={{ fontSize: "13px", color: "#a0aec0", marginTop: "4px" }}>
                {formatPercent(statsData.stats.unique_open_rate)} open rate
              </div>
            </div>

            <div
              style={{
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  color: "#718096",
                  fontSize: "13px",
                  fontWeight: 500,
                  marginBottom: "8px",
                }}
              >
                <MousePointerClick size={16} />
                Unique Clicks
                <div className="tooltip-container" style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
                  <InfoIcon style={{ width: "14px", height: "14px", opacity: 0.4, cursor: "help" }} />
                  <div className="tooltip">Total number of click events recorded for this subscriber.</div>
                </div>
              </div>
              <div style={{ fontSize: "28px", fontWeight: 700, color: "#1a202c" }}>
                {statsData.stats.unique_clicks}
              </div>
              <div style={{ fontSize: "13px", color: "#a0aec0", marginTop: "4px" }}>
                {formatPercent(statsData.stats.unique_click_rate)} click rate
              </div>
            </div>

            <div
              style={{
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  color: "#718096",
                  fontSize: "13px",
                  fontWeight: 500,
                  marginBottom: "8px",
                }}
              >
                <BarChart3 size={16} />
                Total Opens
              </div>
              <div style={{ fontSize: "28px", fontWeight: 700, color: "#1a202c" }}>
                {statsData.stats.total_opens}
              </div>
            </div>

            <div
              style={{
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  color: "#718096",
                  fontSize: "13px",
                  fontWeight: 500,
                  marginBottom: "8px",
                }}
              >
                <ShieldCheck size={16} />
                Verified Clicks
                <div className="tooltip-container" style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
                  <InfoIcon style={{ width: "14px", height: "14px", opacity: 0.4, cursor: "help" }} />
                  <div className="tooltip">Verified clicks use a proprietary formula to confirm that a subscriber has actually clicked. This represents a lower bound on real clicks. Email security systems at universities, large companies, and other organizations may automatically click links, which can make it impossible to detect who actually clicked.</div>
                </div>
              </div>
              <div style={{ fontSize: "28px", fontWeight: 700, color: "#1a202c" }}>
                {statsData.stats.verified_clicks}
              </div>
              <div style={{ fontSize: "13px", color: "#a0aec0", marginTop: "4px" }}>
                {formatPercent(statsData.stats.verified_click_rate)} verified click rate
              </div>
            </div>
          </>
        ) : null}
      </div>

      {/* Two-column layout: Subscriber overview (left) + Activity (right) */}
      <div style={{ display: "flex", gap: "24px", alignItems: "flex-start" }}>
        {/* Left column: Subscriber overview */}
        <div style={{ width: "340px", flexShrink: 0 }}>
          <div
            style={{
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              padding: "20px",
              marginBottom: "16px",
            }}
          >
            <h3 style={{ margin: "0 0 16px 0", fontSize: "16px", fontWeight: 600 }}>Subscriber overview</h3>

            {statsLoading ? (
              <>
                <div style={{ marginBottom: "16px" }}>
                  <SkeletonBlock width="80px" height="12px" style={{ marginBottom: "4px" }} />
                  <SkeletonBlock width="140px" height="14px" />
                </div>
                <div style={{ marginBottom: "16px" }}>
                  <SkeletonBlock width="60px" height="12px" style={{ marginBottom: "4px" }} />
                  <SkeletonBlock width="100px" height="14px" />
                </div>
                <div style={{ marginBottom: "16px" }}>
                  <SkeletonBlock width="90px" height="12px" style={{ marginBottom: "4px" }} />
                  <SkeletonBlock width="120px" height="14px" />
                </div>
              </>
            ) : statsData ? (
              <>
                <div style={{ marginBottom: "16px" }}>
                  <div style={{ fontSize: "12px", color: "#718096", fontWeight: 500, marginBottom: "2px" }}>Subscribe date</div>
                  <div style={{ fontSize: "14px", color: "#1a202c" }}>{formatDate(statsData.subscribed_at)}</div>
                </div>

                {statsData.source && (
                  <div style={{ marginBottom: "16px" }}>
                    <div style={{ fontSize: "12px", color: "#718096", fontWeight: 500, marginBottom: "2px" }}>Source</div>
                    <div style={{ fontSize: "14px", color: "#1a202c" }}>{statsData.source}</div>
                  </div>
                )}

                {statsData.last_delivered_at && (
                  <div style={{ marginBottom: "16px" }}>
                    <div style={{ fontSize: "12px", color: "#718096", fontWeight: 500, marginBottom: "2px" }}>Last delivered</div>
                    <div style={{ fontSize: "14px", color: "#1a202c" }}>{formatDate(statsData.last_delivered_at)}</div>
                  </div>
                )}

                {statsData.last_opened_at && (
                  <div>
                    <div style={{ fontSize: "12px", color: "#718096", fontWeight: 500, marginBottom: "2px" }}>Last opened</div>
                    <div style={{ fontSize: "14px", color: "#1a202c" }}>{formatDate(statsData.last_opened_at)}</div>
                  </div>
                )}
              </>
            ) : null}
          </div>

          {/* Custom Fields */}
          {subscriberId && (
            <SubscriberCustomFields
              subscriberId={subscriberId}
              customFields={statsData?.custom_fields}
              onUpdate={(fields) =>
                setStatsData((prev) => (prev ? { ...prev, custom_fields: fields } : prev))
              }
            />
          )}

          {/* Segments */}
          {subscriberId && <SubscriberSegments subscriberId={subscriberId} />}
        </div>

        {/* Right column: Activity */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ margin: "0 0 16px 0", fontSize: "16px", fontWeight: 600 }}>
            Activity
          </h3>

          {eventsLoading ? (
            <div
              style={{
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "24px 24px 8px",
              }}
            >
              <SkeletonTimelineEvent />
              <SkeletonTimelineEvent />
              <SkeletonTimelineEvent />
              <SkeletonTimelineEvent isLast />
            </div>
          ) : eventsError ? (
            <div style={{ textAlign: "center", padding: "2rem", color: "#E53E3E" }}>
              Failed to load events. <button onClick={() => window.location.reload()} style={{ color: "#5a67d8", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>Retry</button>
            </div>
          ) : events.length === 0 ? (
            <div
              style={{ textAlign: "center", padding: "3rem", color: "#718096" }}
            >
              No events found for this subscriber.
            </div>
          ) : (
            <div
              style={{
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "24px 24px 8px",
              }}
            >
              {events.map((event, index) => (
                <TimelineEvent
                  key={`${event.timestamp}-${event.type}-${index}`}
                  event={event}
                  isLast={index === events.length - 1 && !hasMore}
                  getEventIcon={getEventIcon}
                  getEventIconColor={getEventIconColor}
                  getEventLabel={getEventLabel}
                  formatTimestamp={formatTimestamp}
                />
              ))}

              {hasMore && (
                <div style={{ paddingLeft: "40px", paddingBottom: "16px" }}>
                  <button
                    onClick={loadMoreEvents}
                    disabled={eventsLoadingMore}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      color: eventsLoadingMore ? "#a0aec0" : "#718096",
                      fontSize: "14px",
                      fontWeight: 500,
                      cursor: eventsLoadingMore ? "default" : "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                    onMouseEnter={(e) => {
                      if (!eventsLoadingMore) e.currentTarget.style.color = "#1a202c";
                    }}
                    onMouseLeave={(e) => {
                      if (!eventsLoadingMore) e.currentTarget.style.color = "#718096";
                    }}
                  >
                    {eventsLoadingMore ? (
                      <>
                        <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} />
                        Loading...
                      </>
                    ) : (
                      "Show More"
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TimelineEvent: React.FC<{
  event: SubscriberEvent;
  isLast: boolean;
  getEventIcon: (type: string) => React.ReactNode;
  getEventIconColor: (type: string) => string;
  getEventLabel: (type: string) => string;
  formatTimestamp: (ts: string) => string;
}> = ({
  event,
  isLast,
  getEventIcon,
  getEventIconColor,
  getEventLabel,
  formatTimestamp,
}) => {
    const iconColor = getEventIconColor(event.type);
    const editionTitle = event.edition_title || `Edition #${event.edition_id}`;
    const isClick = event.type.toLowerCase() === "clicked";

    return (
      <div style={{ display: "flex", gap: "16px" }}>
        {/* Timeline line + icon */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "24px",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              border: "2px solid #e2e8f0",
              background: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: iconColor,
              flexShrink: 0,
            }}
          >
            {getEventIcon(event.type)}
          </div>
          {!isLast && (
            <div
              style={{
                width: "2px",
                flexGrow: 1,
                background: "#e2e8f0",
                minHeight: "24px",
              }}
            />
          )}
        </div>

        {/* Content */}
        <div style={{ flex: 1, paddingBottom: isLast ? "8px" : "24px", minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              gap: "12px",
              paddingTop: "5px",
            }}
          >
            <div style={{ fontSize: "14px", minWidth: 0 }}>
              <span style={{ fontWeight: 600, color: "#1a202c" }}>
                {getEventLabel(event.type)}
              </span>
              {isClick && event.verified && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "3px",
                    marginLeft: "6px",
                    padding: "1px 6px",
                    borderRadius: "4px",
                    fontSize: "11px",
                    fontWeight: 600,
                    backgroundColor: "#d1fae5",
                    color: "#065f46",
                  }}
                >
                  <ShieldCheck style={{ width: "12px", height: "12px" }} />
                  Verified
                </span>
              )}
              {isClick && event.verified === false && (
                <span className="tooltip-container" style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: "3px", marginLeft: "6px", padding: "1px 6px", borderRadius: "4px", fontSize: "11px", fontWeight: 600, backgroundColor: "#fef3c7", color: "#92400e" }}>
                  <Bot style={{ width: "12px", height: "12px" }} />
                  Possible bot
                  <span className="tooltip">This click may have been generated by a bot. Email security systems at universities, large companies, and organizations with virus-scanning settings often automatically click links in emails.</span>
                </span>
              )}{" "}
              <span style={{ color: "#5a67d8" }}>{editionTitle}</span>
            </div>
            <span
              style={{
                fontSize: "13px",
                color: "#a0aec0",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {formatTimestamp(event.timestamp)}
            </span>
          </div>
          {isClick && event.original_link && (
            <div
              style={{
                fontSize: "13px",
                color: "#718096",
                marginTop: "4px",
                wordBreak: "break-all",
                paddingLeft: "2px",
              }}
            >
              {event.original_link}
            </div>
          )}
        </div>
      </div>
    );
  };

export default SubscriberDetail;
